import type { OnboardingV2DealSizeUnavailableReason } from "@redotech/admin-sdk/rpc/schema/onboardings-v2/get-onboardings-v2";
import type { CustomRange } from "./types";

const dealSizeFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const lastActivityFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DEFAULT_CUSTOM_RANGE_DAYS = 30;

export const createDefaultCustomRange = (): CustomRange => {
  const now = new Date();
  const start = new Date(now.getTime() - DEFAULT_CUSTOM_RANGE_DAYS * DAY_IN_MS);

  return { start: start.toISOString(), end: now.toISOString() };
};

export const formatDealSize = (value: number | null): string => {
  if (value == null) {
    return "—";
  }
  return dealSizeFormatter.format(value);
};

export const formatDealSizeStatus = (
  value: number | null,
  unavailableReason: OnboardingV2DealSizeUnavailableReason | null,
): string => {
  if (value != null) {
    return formatDealSize(value);
  }
  return unavailableReason == null ? "—" : "N/A";
};

export const formatDuration = (durationSeconds: number): string => {
  if (!durationSeconds || durationSeconds === 0) {
    return "—";
  }

  if (durationSeconds < 60) {
    // Less than 60 seconds - show as seconds
    return `${durationSeconds}s`;
  }

  const totalMinutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;

  if (totalMinutes < 60) {
    // Less than 60 minutes - show as minutes (and seconds if > 0)
    if (seconds > 0) {
      return `${totalMinutes}m ${seconds}s`;
    }
    return `${totalMinutes}m`;
  }

  // 60+ minutes - show as hours and minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${hours}h`;
};

export const formatLastActivity = (date: Date): string =>
  lastActivityFormatter.format(date);

export const toTitleCase = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const formatProcessingDelay = (delayDays: number) => {
  if (!delayDays || delayDays === 0) {
    return "0 days";
  }

  return `${delayDays} day${delayDays === 1 ? "" : "s"}`;
};
